import { OutputGraphSchema } from "@automatry/schemas";
import { ENGINE_TIMESTAMP, uniqueSourceRefs } from "./strategyContext.js";
export function buildOutputGraph(snapshot) {
    const deviceInstances = snapshot.apartmentSolutions.flatMap((solution) => solution.devices.map((device) => ({
        id: device.id,
        specItemRef: device.id,
        productPartNo: device.productPartNo,
        label: makeDeviceLabel(device.role, device.ownerRef),
        quantity: device.quantity,
        apartmentTypeId: solution.apartmentTypeId,
        metadata: {
            family: device.family,
            role: device.role,
            ownerRef: device.ownerRef ?? null,
            pointDemandRefs: device.pointDemandRefs,
            ...(device.valueEngineering
                ? { valueEngineering: device.valueEngineering }
                : {})
        },
        sourceRefs: device.sourceRefs,
        ruleTraceRefs: device.ruleTraceRefs
    })));
    const mappingScheduleRows = snapshot.apartmentSolutions.flatMap(makeMappingScheduleRows);
    const commissioningTasks = snapshot.apartmentSolutions.flatMap((solution) => [
        ...makeCommissioningTasks(solution, snapshot.projectId),
        ...makeInfrastructureCommissioningTasks(solution, snapshot.projectId)
    ]);
    return OutputGraphSchema.parse({
        id: `output-graph-${snapshot.id}`,
        orgId: snapshot.orgId,
        projectId: snapshot.projectId,
        version: snapshot.version,
        specSnapshotId: snapshot.id,
        status: snapshot.status === "rejected" ? "blocked" : "ready",
        deviceInstances,
        mappingEdges: [],
        mappingScheduleRows,
        commissioningTasks,
        requirementSatisfactionClaims: [],
        complianceRows: [],
        requirementTraceabilityRows: [],
        exportBundleIds: [],
        productQuantityTotals: buildProductQuantityTotals(snapshot.apartmentSolutions),
        createdAt: ENGINE_TIMESTAMP,
        sourceRefs: uniqueSourceRefs(snapshot.apartmentSolutions.flatMap((solution) => solution.sourceRefs)),
        ruleTraceRefs: [
            ...new Set(snapshot.apartmentSolutions.flatMap((solution) => solution.ruleTraceRefs))
        ]
    });
}
function makeMappingScheduleRows(solution) {
    const deviceIds = new Set(solution.devices.map((device) => device.id));
    return solution.mappings
        .filter((mapping) => deviceIds.has(mapping.deviceRef))
        .map((mapping) => {
        const zone = solution.logicalZones.find((candidate) => candidate.id === mapping.zoneId);
        return {
            id: `mapping-row-${mapping.id}`,
            apartmentTypeId: solution.apartmentTypeId,
            deviceInstanceId: mapping.deviceRef,
            ...(mapping.logicalControllerRef
                ? { logicalControllerRef: mapping.logicalControllerRef }
                : {}),
            ...(mapping.physicalTerminationRef
                ? { physicalTerminationRef: mapping.physicalTerminationRef }
                : {}),
            ...((zone?.roomName ?? zone?.label)
                ? { room: zone?.roomName ?? zone?.label }
                : {}),
            ...(mapping.cableImplication
                ? { cableImplication: mapping.cableImplication }
                : {}),
            commissioningNote: mapping.commissioningNote,
            riskRefs: mapping.risk.riskRefs,
            sourceRefs: mapping.sourceRefs,
            ruleTraceRefs: mapping.ruleTraceRefs
        };
    });
}
function makeCommissioningTasks(solution, projectId) {
    const deviceIds = new Set(solution.devices.map((device) => device.id));
    return solution.mappings
        .filter((mapping) => deviceIds.has(mapping.deviceRef))
        .map((mapping) => {
        const device = solution.devices.find((candidate) => candidate.id === mapping.deviceRef);
        const zone = solution.logicalZones.find((candidate) => candidate.id === mapping.zoneId);
        return {
            id: `commission-${mapping.id}`,
            projectId,
            title: `Commission ${device?.productPartNo ?? mapping.deviceRef} for ${zone?.label ?? zone?.id ?? solution.apartmentTypeId}`,
            description: mapping.commissioningNote,
            deviceInstanceIds: [mapping.deviceRef],
            status: "planned",
            riskRefs: mapping.risk.riskRefs,
            sourceRefs: mapping.sourceRefs,
            ruleTraceRefs: mapping.ruleTraceRefs
        };
    });
}
function makeInfrastructureCommissioningTasks(solution, projectId) {
    return solution.infrastructure.filter(isThirdPartyAvConnectivityLink).map((item) => ({
        id: `commission-${item.id}`,
        projectId,
        title: `Issue Crestron/third-party AV point mapping for ${solution.apartmentTypeId}`,
        description: item.installNotes?.join(" ") ??
            item.connectivity ??
            "Provide third-party AV/home-automation connectivity details during commissioning.",
        deviceInstanceIds: [],
        status: "planned",
        riskRefs: item.risk.riskRefs,
        sourceRefs: item.sourceRefs,
        ruleTraceRefs: item.ruleTraceRefs
    }));
}
function isThirdPartyAvConnectivityLink(item) {
    return (item.capabilityRole === "third_party_av_connectivity_link" ||
        item.ruleTraceRefs.includes("trace-third-party-av-connectivity-link"));
}
function buildProductQuantityTotals(solutions) {
    const totals = new Map();
    for (const solution of solutions) {
        for (const controller of solution.controllers) {
            addCount(totals, controller.productPartNo, solution.quantity);
        }
        for (const device of solution.devices) {
            const multiplier = device.provisionScope === "project" ? 1 : solution.quantity;
            addCount(totals, device.productPartNo, device.quantity * multiplier);
        }
        for (const item of solution.infrastructure) {
            if (item.productPartNo && item.selectionStatus !== "covered_by_existing") {
                const multiplier = item.provisionScope === "project" ? 1 : solution.quantity;
                addCount(totals, item.productPartNo, item.quantity * multiplier);
            }
        }
    }
    return Object.fromEntries([...totals.entries()].sort(([left], [right]) => left.localeCompare(right)));
}
function addCount(totals, key, quantity) {
    totals.set(key, (totals.get(key) ?? 0) + quantity);
}
function makeDeviceLabel(role, ownerRef) {
    return ownerRef ? `${role} (${ownerRef})` : role;
}
//# sourceMappingURL=outputGraph.js.map